import { type ChangeEvent, type ReactNode } from "react";
import { type UseFormReturn } from "react-hook-form";
import { type z } from "zod";
import {
  Box,
  RadioConsent,
  Collapse,
  Heading,
  Text,
  Link,
} from "@cruk/cruk-react-components";
import { type suppresionValidation } from "@fwa/src/validation/suppressions";

export type SuppressionValues = {
  suppressions: z.infer<typeof suppresionValidation>;
};

type EmailOptions = z.infer<typeof suppresionValidation>["email"];
type TextOptions = z.infer<typeof suppresionValidation>["text"];
type PostOptions = z.infer<typeof suppresionValidation>["post"];
type PhoneOptions = z.infer<typeof suppresionValidation>["phone"];

export const SuppressionFields = <T extends SuppressionValues>(
  props: UseFormReturn<T> & SuppressionValues & { child?: ReactNode },
) => {
  const { suppressions, child } = props;
  const assertProps = props as unknown as UseFormReturn<SuppressionValues>;
  const { setValue } = assertProps;

  return (
    <>
      <Heading h2>Staying in touch with us</Heading>
      <Text>
        Help bring forward the day when all cancers are cured. Hear about our
        latest breakthroughs, campaigns and how you can support our life saving
        work.
      </Text>
      <Text>
        We may contact you by post or phone unless you select ‘No’ below.
      </Text>
      <fieldset
        style={{
          border: "none",
          padding: 0,
          margin: "0 0 var(--spacing-s, 1.5rem) 0",
        }}
      >
        <Heading h4 as="legend">
          Would you like to hear from us by:
        </Heading>
        <RadioConsent
          legend="Email"
          name="suppressions.email"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue("suppressions.email", e.target.value as EmailOptions);
          }}
          attributes={[
            { option: "Yes", value: "PYEM" },
            { option: "No", value: "PNEM" },
          ]}
          selectedValue={suppressions.email}
          data-cta-type="suppressions-email"
        />
        <RadioConsent
          legend="Text message"
          name="suppressions.text"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue("suppressions.text", e.target.value as TextOptions);
          }}
          attributes={[
            { option: "Yes", value: "PYSM" },
            { option: "No", value: "PNSM" },
          ]}
          selectedValue={suppressions.text}
          data-cta-type="suppressions-text"
        />
        <RadioConsent
          legend="Post"
          name="suppressions.post"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue("suppressions.post", e.target.value as PostOptions);
          }}
          attributes={[
            { option: "Yes", value: "LIPO" },
            { option: "No", value: "PNPO" },
          ]}
          selectedValue={suppressions.post}
          data-cta-type="suppressions-post"
        />
        <RadioConsent
          legend="Phone"
          name="suppressions.phone"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue("suppressions.phone", e.target.value as PhoneOptions);
          }}
          attributes={[
            { option: "Yes", value: "LIPH" },
            { option: "No", value: "PNPH" },
          ]}
          selectedValue={suppressions.phone}
          data-cta-type="suppressions-phone"
        />
      </fieldset>
      {child}
      <Heading h3>Change your mind at any time</Heading>
      <Text>
        Contact us on{" "}
        <Link href="tel:+443001233379" data-cta-type="link-tel-contact">
          0300 123 3379
        </Link>{" "}
        or{" "}
        <Link
          href="mailto:preferences@cancer.org.uk"
          rel="nofollow"
          data-cta-type="link-mail-to-preferences"
        >
          preferences@cancer.org.uk
        </Link>{" "}
        or via this{" "}
        <Link
          href="https://www.cancerresearchuk.org/about-us/contact-us/tell-us"
          data-cta-type="link-contact-preferences"
        >
          contact preferences form
        </Link>
        .
      </Text>
      <Text>
        Your details are safe with us. Check out our{" "}
        <Link
          href="https://www.cancerresearchuk.org/privacy-statement"
          data-cta-type="link-contact-privacy-policy"
        >
          Privacy Policy
        </Link>{" "}
        for more details.
      </Text>
      <Text>
        To make our fundraising efforts effective we may research you using
        publicly available sources.
      </Text>
      <Box marginBottom="s">
        <Collapse
          headerTitleText="Find out how we use your information"
          id="notifications-data-use-info"
        >
          <Text>
            Examples of publicly available sources we may use include public
            registers, corporate websites, the news or social media.
          </Text>
          <Text marginBottom="m">
            This information is stored securely and protected from unauthorised
            access or disclosure.
            <br /> Find out more in our{" "}
            <Link
              href="https://www.cancerresearchuk.org/privacy-statement"
              data-cta-type="link-mail-to-privacy-policy"
            >
              privacy policy
            </Link>
            . If you don’t want us to do this research in relation to you, you
            can tell us anytime by contacting 0300 123 3379 or{" "}
            <Link
              href="mailto:preferences@cancer.org.uk"
              rel="nofollow"
              data-cta-type="link-mail-to-preferences"
            >
              preferences@cancer.org.uk
            </Link>
            .
          </Text>
        </Collapse>

        <Text>
          We may try to obtain up-to-date contact details for you using external
          sources if you move house or change number, so that we can stay in
          touch.
        </Text>

        <Collapse
          headerTitleText="Find out more and how to opt out"
          id="notifications-opt-out-info"
        >
          <Text>
            We may try to obtain up-to-date contact details for you using
            external sources such as 3rd party organisations (if you previously
            provided consent to share your data), or the post office national
            change of address database. This means that we can ensure we have an
            accurate record of our supporters and ensure that we continue to
            communicate with you in accordance with your wishes.
          </Text>
          <Text marginBottom="m">
            If you don’t want us to do this in relation to you, you can tell us
            anytime by contacting{" "}
            <Link href="tel:+443001233379">0300 123 3379</Link> or{" "}
            <Link
              href="mailto:preferences@cancer.org.uk"
              rel="nofollow"
              data-cta-type="link-mail-to-preferences"
            >
              preferences@cancer.org.uk
            </Link>
          </Text>
        </Collapse>
      </Box>
    </>
  );
};

export default SuppressionFields;
